import React, { useEffect, useMemo, useState } from 'react'
import * as API from '../../utility/api'
import moment from 'moment'

const ACTIONS = {
  SALES: 'sales',
  DEBT: 'debt'
}

const SALES_VIEWS = {
  EVENTS: 'events',
  SUMMARY: 'summary',
  MONTHS: 'months',
  SELLERS: 'sellers'
}

const Raports = ({ startDate, endDate, setRaportData }) => {
  const [organizers, setOrganizers] = useState([])
  const [events, setEvents] = useState([])
  const [organizerID, setOrganizerID] = useState('')
  const [eventIds, setEventIds] = useState([])
  const [organizerQuery, setOrganizerQuery] = useState('')
  const [eventQuery, setEventQuery] = useState('')
  const [action, setAction] = useState(ACTIONS.SALES)
  const [salesView, setSalesView] = useState(SALES_VIEWS.EVENTS)
  const [includeOrganizer, setIncludeOrganizer] = useState(false)
  const [timeTarget, setTimeTarget] = useState('sold_at')
  const [loading, setLoading] = useState(false)
  const [filtersError, setFiltersError] = useState(null)
  const [eventsError, setEventsError] = useState(null)
  const [sellerID, setSellerID] = useState('')
  const [sellerQuery, setSellerQuery] = useState('')
  const [sellers, setSellers] = useState([])
  const [selectedSeller, setSelectedSeller] = useState(null)
  const [sellerError, setSellerError] = useState(null)

  useEffect(() => {
    let cancelled = false

    API.GET('admin/reports/filters')
      .then(({ data }) => {
        if (cancelled) {
          return
        }
        setOrganizers(data.organizers || [])
        setFiltersError(null)
      })
      .catch(error => {
        if (cancelled) {
          return
        }
        console.error(error)
        setFiltersError('Järjestäjien lataus epäonnistui')
      })

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!organizerID) {
      setEvents([])
      setEventIds([])
      setEventsError(null)
      return
    }

    let cancelled = false

    API.GET('admin/reports/filters', { organizer_id: organizerID })
      .then(({ data }) => {
        if (cancelled) {
          return
        }
        setEvents(data.events || [])
        setEventIds([])
        setEventsError(null)
      })
      .catch(error => {
        if (cancelled) {
          return
        }
        console.error(error)
        setEvents([])
        setEventIds([])
        setEventsError('Tapahtumien lataus epäonnistui')
      })

    return () => {
      cancelled = true
    }
  }, [organizerID])

  useEffect(() => {
    const query = sellerQuery.trim()
    const isId = /^[1-9]\d*$/.test(query)
    if (!isId && query.length < 2) {
      setSellers([])
      setSellerError(null)
      return
    }

    let cancelled = false
    const handle = setTimeout(() => {
      API.GET('admin/reports/filters', { seller_q: query })
        .then(({ data }) => {
          if (cancelled) {
            return
          }
          setSellers(data.sellers || [])
          setSellerError(null)
        })
        .catch(error => {
          if (cancelled) {
            return
          }
          console.error(error)
          setSellers([])
          setSellerError('Myyjien haku epäonnistui')
        })
    }, 300)

    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [sellerQuery])

  const filteredOrganizers = useMemo(() => {
    const query = organizerQuery.trim().toLowerCase()
    if (!query) {
      return organizers
    }

    return organizers.filter(organizer =>
      `${organizer.name} ${organizer.id}`.toLowerCase().includes(query)
    )
  }, [organizers, organizerQuery])

  const filteredEvents = useMemo(() => {
    const query = eventQuery.trim().toLowerCase()
    if (!query) {
      return events
    }

    return events.filter(event =>
      `${event.name} ${event.id}`.toLowerCase().includes(query)
    )
  }, [events, eventQuery])

  const sellerOptions = useMemo(() => {
    const options = selectedSeller ? [selectedSeller] : []
    for (const seller of sellers) {
      if (!options.some(option => String(option.id) === String(seller.id))) {
        options.push(seller)
      }
    }
    return options
  }, [selectedSeller, sellers])

  const fetchData = e => {
    e.preventDefault()

    setLoading(true)
    const startTime = moment(new Date(`${startDate} 00:00:00`))
      .utc()
      .format('YYYY-MM-DD HH:mm:ss')
    const endTime = moment(new Date(`${endDate} 23:59:59`))
      .utc()
      .format('YYYY-MM-DD HH:mm:ss')

    API.POST(`admin/reports/${action}`, {
      start_time: startTime,
      end_time: endTime,
      organizer_id: organizerID || undefined,
      event_ids: eventIds,
      seller_id: sellerID || undefined,
      time_target: timeTarget,
      view: action === ACTIONS.SALES ? salesView : undefined,
      include_organizer: includeOrganizer
    })
      .then(({ data }) => {
        setLoading(false)
        setRaportData(data)
      })
      .catch(error => {
        setLoading(false)
        console.error(error)
      })
  }

  return (
    <form className="admin-panel--raports--form-container" onSubmit={fetchData}>
      <div className="admin-panel--raports--form">
        <label htmlFor="organizerID">Järjestäjä</label>
        <input
          type="search"
          placeholder="Hae järjestäjää nimellä tai ID:llä"
          value={organizerQuery}
          onChange={e => setOrganizerQuery(e.target.value)}
        />
        <select
          id="organizerID"
          value={organizerID}
          onChange={e => setOrganizerID(e.target.value)}
        >
          <option value="">Kaikki järjestäjät</option>
          {filteredOrganizers.map(organizer => (
            <option key={organizer.id} value={organizer.id}>
              {organizer.name} ({organizer.id})
            </option>
          ))}
        </select>
        {filtersError && <p className="raports-form-hint">{filtersError}</p>}

        <div className="break-sm" />

        <label htmlFor="eventIds">Tapahtumat</label>
        <input
          type="search"
          placeholder="Hae tapahtumaa nimellä tai ID:llä"
          value={eventQuery}
          onChange={e => setEventQuery(e.target.value)}
          disabled={!organizerID}
        />
        <select
          id="eventIds"
          multiple
          size={8}
          disabled={!organizerID}
          value={eventIds.map(String)}
          onChange={e =>
            setEventIds(
              Array.from(e.target.selectedOptions, option =>
                Number(option.value)
              )
            )
          }
        >
          {filteredEvents.map(event => (
            <option key={event.id} value={event.id}>
              {event.name} ({event.id})
            </option>
          ))}
        </select>
        {eventsError && <p className="raports-form-hint">{eventsError}</p>}
        <p className="raports-form-hint">
          {organizerID
            ? 'Valitse yksi tai useampi tapahtuma (Cmd/Ctrl + klikkaus). Tyhjä valinta = kaikki järjestäjän tapahtumat.'
            : 'Valitse ensin järjestäjä, jos haluat rajata tapahtumiin. Muuten raportti sisältää kaikki tapahtumat aikavälillä.'}
        </p>

        <div className="break-sm" />

        <label htmlFor="sellerID">Myyjä</label>
        <input
          type="search"
          placeholder="Hae myyjää nimellä, sähköpostilla tai ID:llä"
          value={sellerQuery}
          onChange={e => setSellerQuery(e.target.value)}
        />
        <select
          id="sellerID"
          value={sellerID}
          onChange={e => {
            const nextId = e.target.value
            setSellerID(nextId)
            setSelectedSeller(
              sellerOptions.find(seller => String(seller.id) === nextId) || null
            )
          }}
        >
          <option value="">Kaikki myyjät</option>
          {sellerOptions.map(seller => (
            <option key={seller.id} value={seller.id}>
              {seller.name}
              {seller.email ? ` (${seller.email})` : ''} #{seller.id}
            </option>
          ))}
        </select>
        {sellerError && <p className="raports-form-hint">{sellerError}</p>}
        <p className="raports-form-hint">
          Rajaa myyntiraportti yhteen myyjään. Toimii kaikkien näkymien kanssa.
        </p>

        <div className="break-lg" />

        <h3>Raportin tyyppi</h3>
        <label htmlFor="debt">
          <input
            id="debt"
            className="raport-radio"
            type="radio"
            checked={action === ACTIONS.DEBT}
            onChange={() => setAction(ACTIONS.DEBT)}
          />
          <span style={{ marginLeft: '5px' }}>Velka (sis. alv)</span>
        </label>

        <label htmlFor="sales">
          <input
            id="sales"
            className="raport-radio"
            type="radio"
            checked={action === ACTIONS.SALES}
            onChange={() => setAction(ACTIONS.SALES)}
          />
          <span style={{ marginLeft: '5px' }}>Myynti</span>
        </label>

        {action === ACTIONS.SALES && (
          <>
            <div className="break-lg" />
            <h3>Myyntiraportin näkymä</h3>
            <label htmlFor="view-events">
              <input
                id="view-events"
                className="raport-radio"
                type="radio"
                checked={salesView === SALES_VIEWS.EVENTS}
                onChange={() => setSalesView(SALES_VIEWS.EVENTS)}
              />
              <span style={{ marginLeft: '5px' }}>Tapahtumittain</span>
            </label>
            <label htmlFor="view-summary">
              <input
                id="view-summary"
                className="raport-radio"
                type="radio"
                checked={salesView === SALES_VIEWS.SUMMARY}
                onChange={() => setSalesView(SALES_VIEWS.SUMMARY)}
              />
              <span style={{ marginLeft: '5px' }}>Vain yhteenveto</span>
            </label>
            <label htmlFor="view-months">
              <input
                id="view-months"
                className="raport-radio"
                type="radio"
                checked={salesView === SALES_VIEWS.MONTHS}
                onChange={() => setSalesView(SALES_VIEWS.MONTHS)}
              />
              <span style={{ marginLeft: '5px' }}>Kuukausittain</span>
            </label>
            <label htmlFor="view-sellers">
              <input
                id="view-sellers"
                className="raport-radio"
                type="radio"
                checked={salesView === SALES_VIEWS.SELLERS}
                onChange={() => setSalesView(SALES_VIEWS.SELLERS)}
              />
              <span style={{ marginLeft: '5px' }}>Myyjittäin</span>
            </label>
            <div className="break-sm" />
            <label htmlFor="includeOrganizer">
              <input
                id="includeOrganizer"
                className="raport-radio"
                type="checkbox"
                checked={includeOrganizer}
                onChange={e => setIncludeOrganizer(e.target.checked)}
              />
              <span style={{ marginLeft: '5px' }}>
                Näytä järjestäjätason erittely
              </span>
            </label>
          </>
        )}

        <div className="break-lg" />

        <h3>Ajakohdan kohdistus (Myyntiraportti)</h3>
        <label htmlFor="sold_at">
          <input
            id="sold_at"
            className="raport-radio"
            type="radio"
            checked={timeTarget === 'sold_at'}
            onChange={() => setTimeTarget('sold_at')}
          />
          <span style={{ marginLeft: '5px' }}>Lipun myyntiaika</span>
        </label>

        <label htmlFor="paytrail_payed_at">
          <input
            id="paytrail_payed_at"
            className="raport-radio"
            type="radio"
            checked={timeTarget === 'paytrail_payed_at'}
            onChange={() => setTimeTarget('paytrail_payed_at')}
          />
          <span style={{ marginLeft: '5px' }}>Tilauksen maksuaika</span>
        </label>

        <div className="break-lg" />

        <button disabled={loading} type="submit">
          {loading ? 'Ladataan....' : 'Lataa raportit'}
        </button>
      </div>
    </form>
  )
}

export default Raports
